/**
 * Stats Card Component
 * Displays a single statistic with icon and optional trend
 * Aviation Dashboard Style
 */

import { type ComponentType } from 'react';

type CardColor = 'blue' | 'green' | 'amber' | 'red' | 'purple';

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: ComponentType<{ className?: string }>;
  color?: CardColor;
  trend?: string;
  className?: string;
}

const colorClasses: Record<CardColor, { border: string; icon: string; iconBg: string; glow: string }> = {
  blue: { 
    border: 'border-l-blue-500', 
    icon: 'text-blue-600 dark:text-blue-400', 
    iconBg: 'bg-blue-100 dark:bg-blue-500/10',
    glow: 'shadow-blue-500/10'
  },
  green: { 
    border: 'border-l-green-500', 
    icon: 'text-green-600 dark:text-green-400', 
    iconBg: 'bg-green-100 dark:bg-green-500/10',
    glow: 'shadow-green-500/10'
  },
  amber: { 
    border: 'border-l-amber-500', 
    icon: 'text-amber-600 dark:text-amber-400', 
    iconBg: 'bg-amber-100 dark:bg-amber-500/10',
    glow: 'shadow-amber-500/10'
  },
  red: { 
    border: 'border-l-red-500', 
    icon: 'text-red-600 dark:text-red-400', 
    iconBg: 'bg-red-100 dark:bg-red-500/10',
    glow: 'shadow-red-500/10'
  },
  purple: { 
    border: 'border-l-purple-500', 
    icon: 'text-purple-600 dark:text-purple-400', 
    iconBg: 'bg-purple-100 dark:bg-purple-500/10',
    glow: 'shadow-purple-500/10'
  },
};

export function StatsCard({
  title,
  value,
  icon: Icon,
  color = 'blue',
  trend,
  className = '',
}: StatsCardProps) {
  const colors = colorClasses[color];

  return (
    <div className={`stat-card border-l-3 ${colors.border} p-5 ${className}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-mono font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-mono font-bold text-slate-800 dark:text-slate-100 mt-2 tabular-nums">{value}</p>
          {trend && (
            <p className="text-xs font-mono text-slate-500 mt-2 flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-slate-500" />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colors.iconBg} backdrop-blur-sm`}>
          <Icon className={`w-6 h-6 ${colors.icon}`} />
        </div>
      </div>
    </div>
  );
}
